import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

export interface ErrorDetails {
  type: 'network' | 'authentication' | 'authorization' | 'server' | 'unknown';
  statusCode: number | null;
  userMessage: string;
  technicalMessage: string;
  recoveryAction: string;
}

@Injectable({
  providedIn: 'root',
})
export class ErrorHandlerService {
  /**
   * Identifica o tipo de erro e retorna mensagens apropriadas
   */
  handleLoginError(error: any): ErrorDetails {
    // Erro de rede - Backend não está respondendo
    if (error.status === 0 || error.statusCode === 0) {
      return {
        type: 'network',
        statusCode: 0,
        userMessage: `❌ Servidor indisponível. Verifique se o backend está rodando em ${environment.apiUrl}`,
        technicalMessage: 'Network Error: Backend não está acessível',
        recoveryAction: 'Inicie o backend com: mvn spring-boot:run',
      };
    }

    // Erro 401 - Credenciais inválidas
    if (error.status === 401) {
      return {
        type: 'authentication',
        statusCode: 401,
        userMessage: '❌ Credenciais inválidas. Verifique seu usuário e senha.',
        technicalMessage: 'Invalid credentials: usuário ou senha incorretos',
        recoveryAction: 'Tente novamente com as credenciais corretas',
      };
    }

    // Erro 403 - Acesso negado (usuário não tem permissão)
    if (error.status === 403) {
      return {
        type: 'authorization',
        statusCode: 403,
        userMessage: '❌ Acesso negado. Seu usuário não tem permissão para fazer login.',
        technicalMessage: 'Access Denied: usuário sem permissão de acesso',
        recoveryAction: 'Contate o administrador do sistema',
      };
    }

    // Erro 500 - Erro interno do servidor
    if (error.status === 500) {
      return {
        type: 'server',
        statusCode: 500,
        userMessage: '❌ Erro no servidor. Tente novamente em alguns momentos.',
        technicalMessage: 'Internal Server Error: erro no backend',
        recoveryAction: 'Verifique os logs do backend e tente novamente',
      };
    }

    // Erro 503 - Serviço indisponível
    if (error.status === 503) {
      return {
        type: 'server',
        statusCode: 503,
        userMessage: '❌ Servidor temporariamente indisponível. Tente novamente em alguns momentos.',
        technicalMessage: 'Service Unavailable: servidor está em manutenção',
        recoveryAction: 'Aguarde alguns momentos e tente novamente',
      };
    }

    // Timeout - Requisição demorou muito
    if (error.name === 'TimeoutError' || error.status === 408) {
      return {
        type: 'network',
        statusCode: 408,
        userMessage: '⏱️ A requisição expirou. Verifique a conexão com o servidor.',
        technicalMessage: 'Request Timeout: backend não respondeu a tempo',
        recoveryAction: 'Tente novamente com uma conexão melhor',
      };
    }

    // Erro genérico desconhecido
    return {
      type: 'unknown',
      statusCode: error.status || null,
      userMessage: `❌ Erro desconhecido (${error.status || 'desconhecido'}). Tente novamente.`,
      technicalMessage: `Unknown Error: ${error.message || 'erro não identificado'}`,
      recoveryAction: 'Verifique os logs do navegador e tente novamente',
    };
  }

  /**
   * Obtém apenas a mensagem de erro para exibir ao usuário
   */
  getErrorMessage(error: any): string {
    return this.handleLoginError(error).userMessage;
  }

  /**
   * Obtém a ação de recuperação sugerida
   */
  getRecoveryAction(error: any): string {
    return this.handleLoginError(error).recoveryAction;
  }

  /**
   * Identifica se é erro de rede (backend offline)
   */
  isNetworkError(error: any): boolean {
    return error.status === 0 || this.handleLoginError(error).type === 'network';
  }

  /**
   * Identifica se é erro de credenciais
   */
  isAuthenticationError(error: any): boolean {
    return error.status === 401 || this.handleLoginError(error).type === 'authentication';
  }

  /**
   * Determina se a mensagem retornada pelo backend indica problema com tenant/X-Tenant.
   * Centraliza a regra para que os componentes possam decidir se mostram a dica sobre tenant.
   */
  shouldShowTenantHint(error: any): boolean {
    if (!error) return false;
    // combine common locations where backend error text may appear
    const parts = [error?.message, error?.error, error?.body, error?.error?.message, error?.statusText];
    const text = parts.filter(Boolean).join(' ').toLowerCase();
    if (!text) return false;
    return (
      text.includes('tenant') ||
      text.includes('x-tenant') ||
      text.includes('x-tenant-id') ||
      text.includes('header não fornecido') ||
      text.includes('header nao fornecido') ||
      text.includes('tenant não') ||
      text.includes('tenant nao')
    );
  }

  /**
   * Registra erro completo no console para debug
   */
  logErrorDetails(error: any): void {
    const details = this.handleLoginError(error);
    console.group('🔴 ERRO DE LOGIN');
    console.log('Tipo:', details.type);
    console.log('Status Code:', details.statusCode);
    console.log('Mensagem Técnica:', details.technicalMessage);
    console.log('Erro Completo:', error);
    console.groupEnd();
  }
}
